import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from './supabaseClient'
import { Icon } from '@iconify/react'
import PlanCard from './components/PlanCard'
import { showToast } from './Toast'
import './Onboarding.css'

export default function Onboarding() {
  const ASAAS_BACKEND_URL = '/api/asaas'
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [step, setStep] = useState(1)

  // Data States
  const [businessType, setBusinessType] = useState('')
  const [legalType, setLegalType] = useState('') // 'PF' or 'PJ'
  
  // Step 3 Data
  const [document, setDocument] = useState('') // CPF or CNPJ
  const [companyData, setCompanyData] = useState({
    razaoSocial: '',
    nomeFantasia: '',
    email: '',
    telefone: ''
  })
  const [personalData, setPersonalData] = useState({
    nome: '',
    email: '',
    telefone: ''
  })

  // Step 4 Phone Verification
  const [phone, setPhone] = useState('')
  const [smsCode, setSmsCode] = useState('')
  const [generatedCode, setGeneratedCode] = useState('') // Insecure but MVP
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [faturamento, setFaturamento] = useState('')

  // Step 5 Address
  const [cep, setCep] = useState('')
  const [address, setAddress] = useState({
    rua: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: '',
    complemento: ''
  })

  // Step 7 Plan
  const [selectedPlan, setSelectedPlan] = useState('starter')

  // Load User
  useEffect(() => {
    const loadUser = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        navigate('/login')
        return
      }
      setUser(user)
      
      // Pre-fill email/CPF from metadata if available
      if (user.user_metadata) {
        setPersonalData(prev => ({
          ...prev,
          email: user.email || '',
          nome: user.user_metadata.nome_completo !== 'A definir' ? user.user_metadata.nome_completo : ''
        }))
        setCompanyData(prev => ({
          ...prev,
          email: user.email || ''
        }))
        if (user.user_metadata.cpf_cnpj) {
          setDocument(user.user_metadata.cpf_cnpj)
        }
      }
      setLoading(false)
    }
    loadUser()
  }, [navigate])

  // --- Helpers ---
  const formatDocument = (val) => {
    const v = val.replace(/\D/g, '')
    if (v.length <= 11) { // CPF
      return v.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    } else { // CNPJ
      return v.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    }
  }

  const formatPhone = (val) => {
    const v = val.replace(/\D/g, '')
    if (v.length > 11) return v.slice(0, 11)
    if (v.length > 10) return v.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    if (v.length > 6) return v.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3')
    if (v.length > 2) return v.replace(/^(\d{2})(\d{0,5})/, '($1) $2')
    return v
  }

  const formatCEP = (val) => {
    const v = val.replace(/\D/g, '')
    return v.replace(/^(\d{5})(\d{3})/, '$1-$2')
  }

  // --- Handlers ---

  const handleStep1 = (type) => {
    setBusinessType(type)
    setStep(2)
  }

  const handleStep2 = (type) => {
    setLegalType(type)
    setStep(3)
  }

  const fetchCNPJ = async () => {
    const cleanDoc = document.replace(/\D/g, '')
    if (cleanDoc.length !== 14) {
      showToast('CNPJ inválido', 'error')
      return
    }
    setLoading(true)
    try {
      const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cleanDoc}`)
      if (!res.ok) throw new Error('CNPJ não encontrado')
      const data = await res.json()
      
      setCompanyData(prev => ({
        ...prev,
        razaoSocial: data.razao_social,
        nomeFantasia: data.nome_fantasia || data.razao_social,
        email: data.email || prev.email,
        telefone: data.ddd_telefone_1 || prev.telefone
      }))
      
      // Auto fill address if available
      if (data.cep) {
        setCep(formatCEP(data.cep))
        setAddress(prev => ({
          ...prev,
          rua: data.logradouro,
          numero: data.numero,
          bairro: data.bairro,
          cidade: data.municipio,
          uf: data.uf,
          complemento: data.complemento
        }))
      }
      showToast('Dados da empresa encontrados!', 'success')
    } catch (error) {
      showToast('Erro ao buscar CNPJ. Preencha manualmente.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleStep3Next = () => {
    // Validate
    if (legalType === 'PJ') {
      if (!companyData.razaoSocial) {
        showToast('Preencha a Razão Social', 'error')
        return
      }
    } else {
      if (!personalData.nome) {
        showToast('Preencha seu nome', 'error')
        return
      }
    }
    // Set phone for next step verification
    const phoneToVerify = legalType === 'PJ' ? companyData.telefone : personalData.telefone
    setPhone(formatPhone(phoneToVerify))
    setStep(4)
  }

  const sendSmsCode = async () => {
    const cleanPhone = phone.replace(/\D/g, '')
    if (cleanPhone.length < 10) {
      showToast('Telefone inválido', 'error')
      return
    }
    setLoading(true)
    try {
      const { data, error } = await supabase.functions.invoke('verify-phone', {
        body: { number: cleanPhone }
      })
      if (error) throw error
      // The user showed data.code in the response.
      if (data.code) {
        setGeneratedCode(data.code)
        showToast('SMS enviado com sucesso!', 'success')
      } else {
        // Fallback if API changed
        showToast('Erro ao enviar SMS (sem código)', 'error')
      }
    } catch (error) {
      console.error(error)
      showToast('Erro ao conectar com serviço de SMS', 'error')
    } finally {
      setLoading(false)
    }
  }

  const verifySmsCode = () => {
    if (smsCode === generatedCode && generatedCode !== '') {
      setPhoneVerified(true)
      showToast('Telefone verificado!', 'success')
    } else {
      showToast('Código incorreto', 'error')
    }
  }

  const handleStep4Next = () => {
    if (!phoneVerified) {
      showToast('Por favor, verifique seu telefone', 'error')
      return
    }
    if (!faturamento) {
      showToast('Informe o faturamento aproximado', 'error')
      return
    }
    setStep(5)
  }

  const fetchCEP = async () => {
    const cleanCep = cep.replace(/\D/g, '')
    if (cleanCep.length !== 8) return
    setLoading(true)
    try {
      const res = await fetch(`https://opencep.com/v1/${cleanCep}`)
      if (!res.ok) throw new Error('CEP não encontrado')
      const data = await res.json()
      setAddress(prev => ({
        ...prev,
        rua: data.logradouro,
        bairro: data.bairro,
        cidade: data.localidade,
        uf: data.uf
      }))
    } catch (error) {
      showToast('CEP não encontrado', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleStep5Next = () => {
    if (!address.rua || !address.numero) {
      showToast('Preencha o endereço completo', 'error')
      return
    }
    setStep(6)
  }

  // Step 6: Processing & Account Creation
  const [processing, setProcessing] = useState(false)
  const [canContinue, setCanContinue] = useState(false)

  useEffect(() => {
    if (step === 6) {
      const processAccount = async () => {
        setProcessing(true)
        try {
          // 1. Prepare Data
          const finalName = legalType === 'PJ' ? companyData.razaoSocial : personalData.nome
          const finalDoc = document
          const finalEmail = legalType === 'PJ' ? companyData.email : personalData.email
          const finalPhone = phone
          
          const accountPayload = {
            name: finalName,
            email: finalEmail,
            cpfCnpj: finalDoc.replace(/\D/g, ''),
            companyType: legalType === 'PJ' ? (companyData.tipoEmpresa || 'MEI') : 'MEI',
            phone: finalPhone.replace(/\D/g, ''),
            mobilePhone: finalPhone.replace(/\D/g, ''),
            address: address.rua,
            addressNumber: address.numero,
            complement: address.complemento || '',
            province: address.bairro,
            postalCode: cep.replace(/\D/g, ''),
            site: ''
          }

          if (legalType !== 'PJ' || finalDoc.replace(/\D/g, '').length !== 14) {
            throw new Error('Subconta Asaas: apenas PJ com CNPJ válido')
          }

          const resRegister = await fetch(`${ASAAS_BACKEND_URL}/account-create.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(accountPayload)
          })
          const resultRegister = await resRegister.json()
          if (!resRegister.ok) throw new Error(resultRegister.error || 'Erro ao criar subconta Asaas')
          
          // 3. Update Local User Data (if not already done by Edge Function)
          // The edge function already updates 'usuarios' table!
          
          // 4. Update 'onboarding_completed' flag? 
          // Not strictly necessary if we rely on plan status, but good practice.
          await supabase.from('usuarios').update({
             nome_completo: finalName,
             telefone: finalPhone,
             cpf_cnpj: finalDoc,
             faturamento_mensal: faturamento,
             tipo_negocio: businessType
          }).eq('id', user.id)

          console.log('Subconta criada:', resultRegister)

        } catch (error) {
          console.error('Erro no processamento:', error)
          showToast('Houve um erro ao criar sua conta. Tente novamente.', 'error')
          // Optional: handle retry or allow skip?
        } finally {
          setProcessing(false)
          // Wait 2 seconds before showing button
          setTimeout(() => {
            setCanContinue(true)
          }, 2000)
        }
      }
      processAccount()
    }
  }, [step])

  const handleStep7Next = async () => {
    setLoading(true)
    try {
        // Mark onboarding as completed
        const { error } = await supabase
            .from('usuarios')
            .update({ 
                onboarding_completed: true,
                plano: selectedPlan // Update plan as well
            })
            .eq('id', user.id)

        if (error) throw error

        showToast(`Plano ${selectedPlan} selecionado! Redirecionando para pagamento...`, 'success')
        
        // Simulate payment success and redirect to app
        setTimeout(() => {
            navigate('/app')
        }, 1500)
    } catch (error) {
        console.error('Erro ao finalizar onboarding:', error)
        showToast('Erro ao salvar dados. Tente novamente.', 'error')
    } finally {
        setLoading(false)
    }
  }

  if (loading && !user) return <div className="onboarding-page">Carregando...</div>

  return (
    <div className="onboarding-page">
      <div className="onboarding-card">
        {step > 1 && step < 6 && (
          <button className="onboarding-btn-skip" onClick={() => setStep(step - 1)}>
            <Icon icon="mdi:arrow-left" /> Voltar
          </button>
        )}

        {/* STEP 1: Tipo de Negócio */}
        {step === 1 && (
          <>
            <div className="onboarding-step-header">
              <h2>Qual o seu perfil?</h2>
              <p>Selecione a opção que melhor descreve você</p>
            </div>
            <div className="onboarding-grid">
              {[
                { id: 'agencia', label: 'Agência', sub: 'Agência ou Eugência Digital', icon: 'mdi:office-building-outline' },
                { id: 'freelancer', label: 'Freelancer', sub: 'Profissional Autônomo', icon: 'mdi:account-tie-outline' },
                { id: 'consultor', label: 'Consultor', sub: 'Consultorias em Geral', icon: 'mdi:briefcase-outline' },
                { id: 'influencer', label: 'Influencer', sub: 'Influenciador ou Produtor', icon: 'mdi:star-outline' },
                { id: 'mentor', label: 'Mentor', sub: 'Vende Mentorias ou Cursos', icon: 'mdi:school-outline' },
                { id: 'outro', label: 'Outro', sub: 'Outro tipo de negócio', icon: 'mdi:dots-horizontal' }
              ].map(item => (
                <div 
                  key={item.id} 
                  className={`onboarding-selection-card ${businessType === item.id ? 'selected' : ''}`}
                  onClick={() => handleStep1(item.id)}
                >
                  <Icon icon={item.icon} width="32" color={businessType === item.id ? 'var(--action-primary)' : 'var(--text-tertiary)'} style={{ marginBottom: '12px' }} />
                  <span style={{ fontWeight: '600', marginBottom: '4px' }}>{item.label}</span>
                  <span style={{ fontSize: '12px' }}>{item.sub}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* STEP 2: Tipo Legal */}
        {step === 2 && (
          <>
            <div className="onboarding-step-header">
              <h2>Como você atua?</h2>
              <p>Escolha como deseja se cadastrar</p>
            </div>
            <div className="onboarding-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div 
                className={`onboarding-selection-card ${legalType === 'PF' ? 'selected' : ''}`}
                onClick={() => handleStep2('PF')}
              >
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                  <Icon icon="mdi:account-outline" width="24" color="var(--action-primary)" />
                </div>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '8px' }}>Pessoa Física</span>
                <span style={{ fontSize: '13px', textAlign: 'center' }}>Atuo como Autônomo com CPF</span>
              </div>
              <div 
                className={`onboarding-selection-card ${legalType === 'PJ' ? 'selected' : ''}`}
                onClick={() => handleStep2('PJ')}
              >
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                  <Icon icon="mdi:domain" width="24" color="var(--action-primary)" />
                </div>
                <span style={{ fontWeight: '600', fontSize: '16px', marginBottom: '8px' }}>Pessoa Jurídica</span>
                <span style={{ fontSize: '13px', textAlign: 'center' }}>Tenho uma empresa registrada (CNPJ)</span>
              </div>
            </div>
          </>
        )}

        {/* STEP 3: Dados da Empresa/Pessoa */}
        {step === 3 && (
          <>
            <div className="onboarding-step-header">
              <h2>Dados {legalType === 'PJ' ? 'da Empresa' : 'Pessoais'}</h2>
              <p>Preencha os dados para continuarmos</p>
            </div>
            
            <div className="onboarding-field">
              <label>{legalType === 'PJ' ? 'CNPJ' : 'CPF'}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input 
                  value={document}
                  onChange={(e) => setDocument(formatDocument(e.target.value))}
                  placeholder={legalType === 'PJ' ? '00.000.000/0000-00' : '000.000.000-00'}
                />
                {legalType === 'PJ' && (
                  <button 
                    onClick={fetchCNPJ}
                    disabled={loading}
                    className="onboarding-btn-secondary"
                    style={{ width: 'auto', padding: '0 20px', marginTop: 0 }}
                  >
                    {loading ? <Icon icon="eos-icons:loading" /> : <Icon icon="mdi:magnify" />}
                  </button>
                )}
              </div>
            </div>

            {legalType === 'PJ' ? (
              <>
                <div className="onboarding-field">
                  <label>Razão Social</label>
                  <input 
                    value={companyData.razaoSocial}
                    onChange={(e) => setCompanyData({...companyData, razaoSocial: e.target.value})}
                  />
                </div>
                <div className="onboarding-field">
                  <label>Nome Fantasia</label>
                  <input 
                    value={companyData.nomeFantasia}
                    onChange={(e) => setCompanyData({...companyData, nomeFantasia: e.target.value})}
                  />
                </div>
              </>
            ) : (
              <div className="onboarding-field">
                <label>Nome Completo</label>
                <input 
                  value={personalData.nome}
                  onChange={(e) => setPersonalData({...personalData, nome: e.target.value})}
                />
              </div>
            )}

            <button className="onboarding-btn-primary" onClick={handleStep3Next}>
              Continuar
            </button>
          </>
        )}

        {/* STEP 4: Verificação SMS */}
        {step === 4 && (
          <>
            <div className="onboarding-step-header">
              <h2>Verificar Telefone</h2>
              <p>Enviaremos um código SMS para validação</p>
            </div>

            <div className="onboarding-field">
              <label>Celular (WhatsApp)</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input 
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  placeholder="(00) 00000-0000"
                />
                <button 
                  onClick={sendSmsCode}
                  disabled={loading || phoneVerified}
                  className="onboarding-btn-primary"
                  style={{ width: 'auto', marginTop: 0, padding: '0 20px', backgroundColor: phoneVerified ? '#22c55e' : undefined }}
                >
                  {loading ? <Icon icon="eos-icons:loading" /> : phoneVerified ? <Icon icon="mdi:check" /> : 'Enviar'}
                </button>
              </div>
            </div>

            {generatedCode && !phoneVerified && (
              <div className="onboarding-field">
                <label>Código de Verificação</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value)}
                    placeholder="000000"
                  />
                  <button 
                    onClick={verifySmsCode}
                    className="onboarding-btn-secondary"
                    style={{ width: 'auto', marginTop: 0, padding: '0 20px' }}
                  >
                    Verificar
                  </button>
                </div>
              </div>
            )}

            <div className="onboarding-field">
              <label>Faturamento Mensal Aproximado</label>
              <select 
                value={faturamento}
                onChange={(e) => setFaturamento(e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Ate 5k">Até R$ 5.000</option>
                <option value="5k a 10k">R$ 5.000 a R$ 10.000</option>
                <option value="10k a 50k">R$ 10.000 a R$ 50.000</option>
                <option value="Mais de 50k">Acima de R$ 50.000</option>
              </select>
            </div>

            <button className="onboarding-btn-primary" onClick={handleStep4Next}>
              Continuar
            </button>
          </>
        )}

        {/* STEP 5: Endereço */}
        {step === 5 && (
          <>
            <div className="onboarding-step-header">
              <h2>Endereço</h2>
              <p>Onde você está localizado?</p>
            </div>

            <div className="onboarding-field">
              <label>CEP</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input 
                  value={cep}
                  onChange={(e) => setCep(formatCEP(e.target.value))}
                  onBlur={fetchCEP}
                  placeholder="00000-000"
                />
              </div>
            </div>

            <div className="onboarding-fields-row">
              <div className="onboarding-field" style={{ flex: 3 }}>
                <label>Rua</label>
                <input 
                  value={address.rua}
                  onChange={(e) => setAddress({...address, rua: e.target.value})}
                />
              </div>
              <div className="onboarding-field" style={{ flex: 1 }}>
                <label>Número</label>
                <input 
                  value={address.numero}
                  onChange={(e) => setAddress({...address, numero: e.target.value})}
                />
              </div>
            </div>

            <div className="onboarding-fields-row">
              <div className="onboarding-field" style={{ flex: 1 }}>
                <label>Bairro</label>
                <input 
                  value={address.bairro}
                  onChange={(e) => setAddress({...address, bairro: e.target.value})}
                />
              </div>
              <div className="onboarding-field" style={{ flex: 1 }}>
                <label>Cidade</label>
                <input 
                  value={address.cidade}
                  onChange={(e) => setAddress({...address, cidade: e.target.value})}
                />
              </div>
            </div>

             <div className="onboarding-field">
                <label>Estado (UF)</label>
                <input 
                  value={address.uf}
                  onChange={(e) => setAddress({...address, uf: e.target.value})}
                  maxLength={2}
                />
              </div>

            <button className="onboarding-btn-primary" onClick={handleStep5Next}>
              Continuar
            </button>
          </>
        )}

        {/* STEP 6: Welcome / Loading */}
        {step === 6 && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div className="onboarding-step-header">
              <h2>Bem-vindo ao ClickPag! 🎉</h2>
              <p>Estamos preparando sua conta...</p>
            </div>
            
            <div style={{ 
              width: '100%', 
              height: '250px', 
              backgroundColor: 'var(--bg-tertiary)', 
              borderRadius: '12px', 
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid var(--border-primary)'
            }}>
               <Icon icon="mdi:play-circle-outline" width="64" color="var(--text-tertiary)" />
               <span style={{ marginLeft: '10px', color: 'var(--text-tertiary)' }}>Vídeo de Introdução</span>
            </div>

            {processing && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: 'var(--action-primary)', marginBottom: '20px' }}>
                <Icon icon="eos-icons:loading" width="24" />
                <span>Configurando ambiente financeiro...</span>
              </div>
            )}

            <button 
              className="onboarding-btn-primary"
              style={{ opacity: canContinue ? 1 : 0.5, cursor: canContinue ? 'pointer' : 'not-allowed' }}
              disabled={!canContinue}
              onClick={() => setStep(7)}
            >
              Acessar Minha Conta Agora
            </button>
          </div>
        )}

        {/* STEP 7: Plan Selection */}
        {step === 7 && (
          <>
             <div className="onboarding-step-header">
               <h2>Escolha seu Plano</h2>
               <p>Comece grátis por 3 dias. Cancele quando quiser.</p>
             </div>
             
             <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }}>
                <PlanCard
                  plano="starter"
                  preco="R$ 97/mês"
                  limite="200"
                  descricao="Para quem está começando"
                  selected={selectedPlan === 'starter'}
                  onClick={() => setSelectedPlan('starter')}
                />
                <PlanCard
                  plano="pro"
                  preco="R$ 197/mês"
                  limite="1000"
                  descricao="Para empresas em crescimento"
                  popular={true}
                  selected={selectedPlan === 'pro'}
                  onClick={() => setSelectedPlan('pro')}
                />
                <PlanCard
                  plano="premium"
                  preco="R$ 297/mês"
                  limite="Ilimitado"
                  descricao="Para grandes operações"
                  selected={selectedPlan === 'premium'}
                  onClick={() => setSelectedPlan('premium')}
                />
             </div>

             <button className="onboarding-btn-finish onboarding-btn-primary" onClick={handleStep7Next}>
               <Icon icon="mdi:credit-card-outline" style={{ verticalAlign: 'middle', marginRight: '8px' }} />
               Ir para Pagamento
             </button>
          </>
        )}

      </div>
    </div>
  )
}
